import omit from 'lodash/omit';

import { getPageName } from 'zoomforth_codesbane_editor/reducers/index.js';

import {
  getSecondaryLogoData,
  getTranslationsCurrentLanguageIso,
  getTranslationsDefaultLanguageIso
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  ensureThrottledTranslationUpdate
} from './translations.js';

import { ensureThrottledPageConfigSaving } from './config.js';

import { savePageMetaAttrs } from './pageMeta.js';

import {
  SET_IS_HEADER_EDITOR_OPEN,
  SET_IS_IN_PAGE_SEARCH_ENABLED,
  END_ALL_EDITING,
  SET_TEMPORARY_PAGE_TITLE,
  OPEN_PAGE_TITLE_EDITOR,
  CLOSE_PAGE_TITLE_EDITOR,
  SET_IS_SECONDARY_LOGO_ENABLED,
  SET_SECONDARY_LOGO_DATA,
  SET_PAGE_NAME,
  SET_SHOULD_SHOW_HEADER,
  SET_SHOULD_SHOW_TITLE,
  SET_SHOULD_SHOW_SOCIAL_LINKS,
  SET_SOCIAL_LINKS,
  SET_IS_INLINE_HEADER_STYLE,
  SET_DISPLAY_ANCHOR_LINKS,
  SET_IS_FULL_BLEED,
  SET_LAYOUT_HEADER_STYLE,
  SET_HEADER_CONTENT_POSITIONING,
  SET_IS_USING_LEGACY_HEADER,
} from '../action_types.js';

import { get as getPageHistoryTracker } from 'zoomforth_zf/appglobals/page-history-tracker.js';

export const closeHeaderEditor = () => ({
  type: SET_IS_HEADER_EDITOR_OPEN,
  isOpen: false,
});

export const openHeaderEditor = () => (dispatch) => {
  dispatch({
    type: END_ALL_EDITING,
  }),
    dispatch({
      type: SET_IS_HEADER_EDITOR_OPEN,
      isOpen: true,
    });
};

export const openPageTitleEditor = () => (dispatch, getState) => {
  const state = getState();
  const currentLanguageIso = getTranslationsCurrentLanguageIso(state);
  const defaultLanguageIso = getTranslationsDefaultLanguageIso(state);
  const isEditingSecondaryLang = currentLanguageIso !== defaultLanguageIso;

  // we already have the page title masked in a secondary language so we don't
  // need to set it again incorrectly
  if (!isEditingSecondaryLang) {
    const pageTitle = getPageName(getState());

    dispatch({
      type: SET_TEMPORARY_PAGE_TITLE, // Set the temporary title to the current page title
      pageTitle,
    });
  }

  dispatch({
    type: OPEN_PAGE_TITLE_EDITOR,
  });
};

export const closePageTitleEditor = () => (dispatch) => {
  dispatch({
    type: CLOSE_PAGE_TITLE_EDITOR,
  });
};

export const setIsInPageSearchEnabled = (value) => (dispatch) => {
  dispatch({
    type: SET_IS_IN_PAGE_SEARCH_ENABLED,
    value,
  });
  dispatch(ensureThrottledPageConfigSaving());
};

export const setIsSecondaryLogoEnabled = (value) => (dispatch) => {
  dispatch({
    type: SET_IS_SECONDARY_LOGO_ENABLED,
    value,
  });
  dispatch(ensureThrottledPageConfigSaving());
};

export const updateSecondaryLogoData =
  (newSecondaryLogoData) => (dispatch, getState) => {
    const currentSecondaryLogoData = omit(getSecondaryLogoData(getState()), [
      'contentItem',
      'asset',
    ]);

    dispatch(
      setSecondaryLogoData({
        ...currentSecondaryLogoData,
        ...newSecondaryLogoData,
      })
    );
  };

export const setSecondaryLogoData = (secondaryLogoData) => (dispatch) => {
  dispatch({
    type: SET_SECONDARY_LOGO_DATA,
    data: secondaryLogoData,
  });
  dispatch(ensureThrottledPageConfigSaving());
};

export const setPageTitle = (pageName) => (dispatch, getState) => {
  // Skip sending a save request if the name has not changed
  if (getPageName(getState()) === pageName) {
    return;
  }

  dispatch({
    type: SET_PAGE_NAME,
    pageName,
  });
  getPageHistoryTracker().trackChangePageTitle(pageName);

  const state = getState();
  const defaultLanguageIso = getTranslationsDefaultLanguageIso(state);
  const currentLanguageIso = getTranslationsCurrentLanguageIso(state);
  const isEditingSecondaryLang = currentLanguageIso !== defaultLanguageIso;

  if (isEditingSecondaryLang) {
    dispatch(ensureThrottledTranslationUpdate());
  } else {
    dispatch(savePageMetaAttrs(['unpublishedName']));
    dispatch(ensureThrottledTranslationUpdate());
  }
};

export const setTemporaryPageTitle = (pageTitle) => (dispatch) => {
  dispatch({
    type: SET_TEMPORARY_PAGE_TITLE,
    pageTitle,
  });
  dispatch(ensureThrottledPageConfigSaving());
};

export const setShouldShowHeader = (value) => (dispatch) => {
  dispatch({
    type: SET_SHOULD_SHOW_HEADER,
    value,
  });
  dispatch(ensureThrottledPageConfigSaving());
};

export const setShouldShowTitle = (value) => (dispatch) => {
  dispatch({
    type: SET_SHOULD_SHOW_TITLE,
    value,
  });
  dispatch(ensureThrottledPageConfigSaving());
};

export const setShouldShowSocialLinks = (value) => (dispatch) => {
  dispatch({
    type: SET_SHOULD_SHOW_SOCIAL_LINKS,
    value,
  });
  dispatch(ensureThrottledPageConfigSaving());
};

export const setSocialLinks = (socialLinks) => (dispatch) => {
  dispatch({
    type: SET_SOCIAL_LINKS,
    socialLinks,
  });
  dispatch(ensureThrottledPageConfigSaving());
};

/**
 * Action creators for new header editor
 */

export const setIsInlineHeaderStyle = (value) => (dispatch) => {
  dispatch({
    type: SET_IS_INLINE_HEADER_STYLE,
    value,
  });
  dispatch(ensureThrottledPageConfigSaving());
};

export const setDisplayAnchorLinks = (value) => (dispatch) => {
  dispatch({
    type: SET_DISPLAY_ANCHOR_LINKS,
    value,
  });
  dispatch(ensureThrottledPageConfigSaving());
};

export const setIsFullBleed = (value) => (dispatch) => {
  dispatch({
    type: SET_IS_FULL_BLEED,
    value,
  });
  dispatch(ensureThrottledPageConfigSaving());
};

export const setLayoutHeaderStyle = (value) => (dispatch) => {
  dispatch({
    type: SET_LAYOUT_HEADER_STYLE,
    value,
  });
  dispatch(ensureThrottledPageConfigSaving());
};

export const updateHeaderContentPosition =
  ({ position }) =>
  (dispatch) => {
    dispatch({
      type: SET_HEADER_CONTENT_POSITIONING,
      value: position,
    });
    dispatch(ensureThrottledPageConfigSaving());
  };

export const setShouldUseLegacyHeader = (isUsingLegacyHeader) => (dispatch) => {
  dispatch({
    type: SET_IS_USING_LEGACY_HEADER,
    value: isUsingLegacyHeader,
  });
  dispatch(ensureThrottledPageConfigSaving());
};
